// Interfaces
import { IMinMaxLimit, IParams, ITier, ITierComparison, ITierRanges, ITierRow, ITiersLimits } from "./interfaces";

const isSingleLastValid = (tiers: ITierRow[] = [], params: IParams | null = null) => {
	const { reference = "commission" } = params || {};
	const rows = tiers?.length || 1;
	const { [reference as keyof object]: item } = tiers?.[rows - 1] || {};
	return Boolean(item?.value);
};

const isLastValid = (tiers: ITier[] = [], params: IParams | null = null) => {
	const { column = "commissions", reference = "commission" } = params || {};
	const rows = tiers?.length || 1;
	const columns = (tiers?.[0]?.[column] ?? [])?.length || 1;
	const { [reference]: element } = tiers?.[rows - 1]?.[column]?.[columns - 1] || {};
	return Boolean(element?.value);
};

const isFirstValid = (tiers: ITier[] = [], params: IParams | null = null) => {
	const { column = "commissions", reference = "commission" } = params || {};
	const { [reference]: element } = tiers?.[0]?.[column]?.[0] || {};
	return Boolean(element?.value);
};

const isValidMatrixRange = (tiers: ITier[], params: IParams, ranges?: ITierRanges) => {
	const { column = "commissions" } = params || {};
	const { rows = 2, columns = 2 } = ranges || {};
	return tiers?.length >= rows || tiers?.[0]?.[column]?.length >= columns;
};

const compareValues = (v1: number, v2: number, operator = "===") => {
	switch (operator) {
		case "===":
		case "==":
			return v1 === v2;
		case "!==":
			return v1 !== v2;
		case "<":
			return v1 < v2;
		case ">":
			return v1 > v2;
		case "<=":
			return v1 <= v2;
		case ">=":
			return v1 >= v2;
		default:
			break;
	}
	return true;
};

const isValidValue = (tiers: ITier[], params: IParams & ITierComparison) => {
	const { rowIndex = 0, columnIndex = 0, column = "commissions", reference = "commission", value = 0, operator = "===" } = params || {};
	const { [reference]: element } = tiers?.[rowIndex]?.[column]?.[columnIndex] || {};
	return compareValues(element?.value, value, operator);
};

const getTierLastIndex = (tiers: ITier[], params?: Partial<IParams>) => {
	const { column } = params ?? {};
	let length = 0;
	if (column) {
		length = (tiers?.[0]?.[column]?.length ?? 0) - 1;
	} else {
		length = (tiers?.length ?? 0) - 1;
	}
	if (length !== -1) {
		return length;
	}
	return 0;
};

const resetTierInput = (tier: ITierRow, args: { attributes: string[]; valueToSet: number; inputToSet: string }) => {
	const { attributes, valueToSet = 0, inputToSet = "Percent" } = args ?? {};
	return attributes.reduce((acc: ITierRow, attribute) => {
		if (attribute in acc) {
			acc[attribute as keyof ITierRow]["value"] = valueToSet;
			acc[attribute as keyof ITierRow]["input"] = inputToSet;
		}
		return acc;
	}, tier);
};

const resetTiersInputValues = (
	tiers: ITier[],
	args: { params: Partial<IParams>; attributes: string[]; valueToSet: number; inputToSet: string }
) => {
	const { params, attributes, valueToSet = 0, inputToSet = "Percent" } = args ?? {};
	const { column, reference } = params || {};

	const updatedTiers = [...tiers];
	if (column) {
		updatedTiers?.forEach((tier) => {
			tier?.[column]?.forEach((columnTier) => {
				if (reference && reference in columnTier) {
					columnTier[reference] = resetTierInput(columnTier[reference as keyof ITier], { attributes, valueToSet, inputToSet });
				}
				return resetTierInput(columnTier, { attributes, valueToSet, inputToSet });
			});
		});
	} else {
		updatedTiers?.forEach((tier) => {
			return resetTierInput(tier, { attributes, valueToSet, inputToSet });
		});
	}
	return updatedTiers;
};

const setTierLimit = (tiersList: ITierRow[], limits?: IMinMaxLimit) => {
	const { min = 0, max = Number.MAX_VALUE } = limits ?? {};
	if (!tiersList?.length) {
		return tiersList;
	}
	const lastRowsIndex = getTierLastIndex(tiersList);
	(tiersList[0])["from"]["value"] = min;
	(tiersList[lastRowsIndex])["to"]["value"] = max;
	return [...tiersList];
};

const setTiersLimits = (tiers: ITier[], args: { params?: IParams; limits: ITiersLimits; setRows?: boolean; setColumns?: boolean }) => {
	const { params, limits, setRows = false, setColumns = false } = args ?? {};
	const { column } = params ?? {};

	if (!tiers?.length) {
		return tiers;
	}

	let updatedTiers = [...tiers];

	const { rows, columns } = limits ?? {};
	const { min: minRows = 0, max: maxRows = Number.MAX_VALUE } = rows ?? {};
	const { min: minColumns = 0, max: maxColumns = Number.MAX_VALUE } = columns ?? {};

	if (column && setColumns) {
		updatedTiers.forEach((tier, key) => {
			updatedTiers[key][column] = setTierLimit(tier[column], { min: minColumns, max: maxColumns });
		});
	}

	if (setRows) {
		updatedTiers = setTierLimit(updatedTiers, { min: minRows, max: maxRows });
	}

	return updatedTiers;
};

export {
	isFirstValid,
	isLastValid,
	isSingleLastValid,
	isValidMatrixRange,
	isValidValue,
	getTierLastIndex,
	resetTiersInputValues,
	setTiersLimits,
};
